import React, { useState } from "react";
import { Button, CircularProgress, LinearProgress } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import type { Question, QuestionResponse } from "../types";
import { submitResponses } from "../api";
import type { SubmitResponse } from "../types";
import styles from "../SkillGapAssessment.module.scss";

interface QuestionsStepProps {
  workflowId: string;
  questions: Question[];
  onSubmit: (result: SubmitResponse) => void;
  onError: (error: string) => void;
}

interface StoredResponse {
  question_id: string;
  response: number;
  direction?: string;
}

const DEFAULT_OPTIONS = [
  { value: 1, label: "Never", description: "Never" },
  { value: 2, label: "Rarely", description: "Rarely" },
  { value: 3, label: "Sometimes", description: "Sometimes" },
  { value: 4, label: "Often", description: "Often" },
  { value: 5, label: "Always", description: "Always" },
];

function groupBySkill(questions: Question[]): { skill: string; questions: Question[] }[] {
  const map = new Map<string, Question[]>();
  for (const q of questions) {
    const list = map.get(q.skill) ?? [];
    list.push(q);
    map.set(q.skill, list);
  }
  return Array.from(map.entries()).map(([skill, qs]) => ({ skill, questions: qs }));
}

export default function QuestionsStep({
  workflowId,
  questions,
  onSubmit,
  onError,
}: QuestionsStepProps): JSX.Element {
  const [responses, setResponses] = useState<Record<string, StoredResponse>>({});
  const [sectionIndex, setSectionIndex] = useState(0);
  const [loading, setLoading] = useState(false);

  const groups = groupBySkill(questions);
  const totalSections = groups.length;
  const currentGroup = groups[sectionIndex];

  const totalQuestions = questions.length;
  const answeredCount = Object.keys(responses).length;
  const overallProgress = totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;

  const currentSectionAnswered = currentGroup?.questions.every(
    (q) => responses[q.question_id] !== undefined
  ) ?? false;

  const allAnswered = answeredCount === totalQuestions;
  const isLastSection = sectionIndex === totalSections - 1;

  // Running offset so each question shows its global number
  const sectionOffsets = groups.map((_, i) =>
    groups.slice(0, i).reduce((acc, g) => acc + g.questions.length, 0)
  );

  const handleSelect = (question: Question, value: number) => {
    setResponses((prev) => ({
      ...prev,
      [question.question_id]: {
        question_id: question.question_id,
        response: value,
        direction: question.direction,
      },
    }));
  };

  const handleNext = () => {
    if (sectionIndex < totalSections - 1) {
      setSectionIndex((i) => i + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleBack = () => {
    if (sectionIndex > 0) {
      setSectionIndex((i) => i - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSubmit = async () => {
    if (!allAnswered) return;

    const responseArray: QuestionResponse[] = questions.map((q) => ({
      question_id: q.question_id,
      response: responses[q.question_id].response,
      direction: q.direction,
    }));

    setLoading(true);
    onError("");

    try {
      const result = await submitResponses(workflowId, responseArray);
      onSubmit(result);
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to submit responses. Please try again.";
      onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!currentGroup) return <></>;

  const globalOffset = sectionOffsets[sectionIndex];

  return (
    <div className={styles.questionsContainer}>
      {/* Overall progress bar */}
      <div className={styles.progressHeader}>
        <div className={styles.progressMeta}>
          <span className={styles.progressLabel}>
            Section {sectionIndex + 1} of {totalSections}: <strong>{currentGroup.skill}</strong>
          </span>
          <span className={styles.progressLabel}>
            {answeredCount} of {totalQuestions} answered
          </span>
        </div>
        <LinearProgress
          variant="determinate"
          value={overallProgress}
          className={styles.progressBar}
        />
      </div>

      {/* Section header */}
      <div className={styles.sectionHeader}>
        <span className={styles.sectionBadge}>
          Section {sectionIndex + 1} / {totalSections}
        </span>
        <h2 className={styles.sectionTitle}>{currentGroup.skill}</h2>
      </div>

      {/* Questions for this section */}
      {currentGroup.questions.map((question, i) => {
        const selected = responses[question.question_id]?.response;
        const options = question.options?.length ? question.options : DEFAULT_OPTIONS;
        const globalNum = globalOffset + i + 1;

        return (
          <div key={question.question_id} className={styles.questionCard}>
            <div className={styles.questionMeta}>
              <span className={styles.skillTag}>{question.skill}</span>
              <span className={styles.questionCounter}>
                Question {globalNum} of {totalQuestions}
              </span>
            </div>
            <p className={styles.questionText}>{question.text}</p>

            <div className={styles.optionGrid}>
              {options.map((opt) => {
                const isSelected = selected === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`${styles.optionTile} ${isSelected ? styles.optionTileSelected : ""}`}
                    onClick={() => handleSelect(question, opt.value)}
                    data-selected={isSelected}
                  >
                    {isSelected && (
                      <span className={styles.checkIndicator} aria-hidden="true">✓</span>
                    )}
                    <span className={styles.optionDescription}>{opt.description}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Navigation */}
      <div className={styles.sectionNav}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          disabled={sectionIndex === 0 || loading}
        >
          Previous
        </Button>

        <span className={styles.sectionDots}>
          {groups.map((_, i) => (
            <span
              key={i}
              className={`${styles.sectionDot} ${i === sectionIndex ? styles.sectionDotActive : ""} ${
                groups[i].questions.every((q) => responses[q.question_id]) ? styles.sectionDotDone : ""
              }`}
            />
          ))}
        </span>

        {!isLastSection ? (
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={handleNext}
            disabled={!currentSectionAnswered || loading}
          >
            Next Section
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!allAnswered || loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? "Submitting..." : "Submit Assessment"}
          </Button>
        )}
      </div>

      {isLastSection && !allAnswered && (
        <p className={styles.submitHint}>
          Answer all {totalQuestions} questions to submit — {totalQuestions - answeredCount} remaining
        </p>
      )}
    </div>
  );
}
